import {
  Add,
  Button,
  Edit,
  FormCard,
  FormItem,
  Input,
  Trash,
  theme
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import {
  Form as AntdForm,
  InputNumber,
  Dropdown,
  DatePicker,
  TimePicker,
  Select as AntdSelect
} from 'antd'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { getRequiredRule, getIsDifferentRule } from '../../utils'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import {
  OverrideLevel,
  PaymentCard,
  PaymentCardTeamMember,
  RateType
} from '../../hooks/usePaymentCards'
import { DATE_FORMAT, TIME_FORMAT } from '../../utils.datetime'
import { ClockCircleOutlined, CloseOutlined } from '@ant-design/icons'
import PayRateHistoryDrawer from './PayRateHistoryDrawer'
import TeamMembersSection from './TeamMembersSection'
import dayjs from 'dayjs'

const getRateTypeOptions = (t: (key: string) => string) => [
  { value: RateType.HOURLY, label: t('RATE_TYPE_HOURLY') },
  { value: RateType.EVENT, label: t('RATE_TYPE_FIXED') }
]

export const formatRateDisplay = (
  rate: number | string,
  rateType: RateType | undefined,
  t: (key: string) => string
) => {
  const value = typeof rate === 'string' ? parseFloat(rate) : rate
  return rateType === RateType.EVENT
    ? `${value} ${t('RATE_TYPE_FIXED')}`
    : `${value} ${t('RATE_TYPE_HOURLY')}`
}

export type PayRateCardProps = {
  fieldName: number
  form: ReturnType<typeof AntdForm.useForm>[0]
  onDirty: () => void
  onClean: () => void
  originalCard?: any
}

const PayRateCard = ({
  fieldName,
  form,
  onDirty,
  onClean,
  originalCard
}: PayRateCardProps) => {
  const { t } = useTranslation()
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()
  const rateTypeOptions = getRateTypeOptions(t)

  const cardId = form.getFieldValue(['payRates', fieldName, '_id'])
  const [isEditing, setIsEditing] = useState(!cardId)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const initialId = form.getFieldValue(['payRates', fieldName, 'initialId'])
  const hasHistory = !!initialId && initialId !== cardId

  const shiftTypeIds = useMemo(
    () =>
      new Set(
        (shiftTypes || [])
          .filter((shiftType) => shiftType.id)
          .map((shiftType) => shiftType.id!)
      ),
    [shiftTypes]
  )

  const allEventTypes = useMemo(
    () => [
      ...(shiftTypes || [])
        .filter((shiftType) => shiftType.id && !shiftType.isDeleted)
        .map((shiftType) => ({ id: shiftType.id!, name: shiftType.name })),
      ...(visitTypes || [])
        .filter((visitType) => visitType.id && !visitType.isDeleted)
        .map((visitType) => ({ id: visitType.id!, name: visitType.name }))
    ],
    [shiftTypes, visitTypes]
  )

  const eventTypeOptions = useMemo(
    () =>
      allEventTypes.map((eventType) => ({
        value: eventType.id,
        label: <Text>{eventType.name}</Text>
      })),
    [allEventTypes]
  )

  const eventTypeNameMap = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        allEventTypes.map((eventType) => [eventType.id, eventType.name])
      ),
    [allEventTypes]
  )

  const name = AntdForm.useWatch(['payRates', fieldName, 'name'], form)
  const cardTitle = name || `${t('PAY_RATES')} ${fieldName + 1}`

  const currentRates =
    form.getFieldValue(['payRates', fieldName, 'rates']) || []
  const usedEventTypeIds = currentRates
    .filter((_rate: any, index: number) => index > 0 && !_rate?.isDeleted)
    .map((rate: any) => rate?.eventTypeId)
    .filter(Boolean)

  const availableOptions = eventTypeOptions.filter(
    (option) => !usedEventTypeIds.includes(option.value)
  )

  const getIsNameDifferentRule = () => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = (formValues.payRates || []).map((payRate: any) =>
      payRate?.isDeleted ? { ...payRate, name: undefined } : payRate
    )
    return getIsDifferentRule(currentFormValues, fieldName)
  }

  const handleCancel = () => {
    const allPayRates = form.getFieldsValue(true).payRates
    allPayRates[fieldName] = { ...originalCard }
    form.setFieldsValue({ payRates: allPayRates })
    setIsEditing(false)
    onClean()
  }

  const handleDelete = () => {
    onDirty()
    form.setFieldValue(['payRates', fieldName, 'isDeleted'], true)
  }

  const handleDeleteRate = (rateIndex: number) => {
    onDirty()
    form.setFieldValue(
      ['payRates', fieldName, 'rates', rateIndex, 'isDeleted'],
      true
    )
  }

  const filterDeletedRates = (rateIndex: number) => {
    const isDeleted = form.getFieldValue([
      'payRates',
      fieldName,
      'rates',
      rateIndex,
      'isDeleted'
    ])
    return isDeleted !== true
  }

  if (!isEditing) {
    const previousVersion: PaymentCard | undefined = form.getFieldValue([
      'payRates',
      fieldName,
      'previousVersion'
    ])

    const effectiveDate = form.getFieldValue([
      'payRates',
      fieldName,
      'effectiveDate'
    ])
    const effectiveTime = form.getFieldValue([
      'payRates',
      fieldName,
      'effectiveTime'
    ])
    const baseRate = form.getFieldValue([
      'payRates',
      fieldName,
      'rates',
      0,
      'rate'
    ])

    const effectiveFromDisplay =
      effectiveDate && effectiveTime
        ? `${dayjs(effectiveDate).format(DATE_FORMAT)}  ${dayjs(effectiveTime).format(TIME_FORMAT)}`
        : '-'

    const teamMembers: PaymentCardTeamMember[] =
      form.getFieldValue(['payRates', fieldName, 'teamMembers']) || []

    const baseRateType = currentRates[0]?.rateType
    const overrideRates = currentRates
      .filter((_rate: any, index: number) => index > 0 && !_rate?.isDeleted)
      .map((rate: any) => ({
        name: eventTypeNameMap[rate.eventTypeId] || rate.eventTypeId,
        rate: rate.rate,
        rateType: rate.rateType
      }))

    const prevBaseRate = previousVersion?.rates.find(
      (r) => r.level === OverrideLevel.BASE
    )
    const prevOverrides =
      previousVersion?.rates.filter((r) => r.level !== OverrideLevel.BASE) || []
    const prevEffectiveFromDisplay = previousVersion?.effectiveFrom
      ? `${dayjs(previousVersion.effectiveFrom).format(DATE_FORMAT)}  ${dayjs(previousVersion.effectiveFrom).format(TIME_FORMAT)}`
      : '-'

    return (
      <ViewFormCard
        level={2}
        title={previousVersion ? previousVersion.name : cardTitle}
        rightChildren={
          <Button
            variant="text"
            type="button"
            icon={<Edit />}
            onClick={() => setIsEditing(true)}
          >
            {previousVersion ? t('EDIT_PLANNED_RATE') : t('EDIT_RATE')}
          </Button>
        }
      >
        {previousVersion && (
          <>
            <ViewSection>
              <ViewLabel level={2}>{t('EFFECTIVE_FROM')}</ViewLabel>
              <ViewValue>{prevEffectiveFromDisplay}</ViewValue>
            </ViewSection>

            <RatesRow>
              <ViewSection>
                <ViewLabel level={2}>{t('BASE_RATE')}</ViewLabel>
                <ViewValue>
                  {prevBaseRate
                    ? formatRateDisplay(
                        prevBaseRate.rate,
                        prevBaseRate.rateType,
                        t
                      )
                    : '-'}
                </ViewValue>
              </ViewSection>

              {prevOverrides.map((override) => (
                <ViewSection key={override.eventTypeId}>
                  <ViewLabel level={2}>
                    {eventTypeNameMap[override.eventTypeId!] ||
                      override.eventTypeId}
                  </ViewLabel>
                  <ViewValue>
                    {formatRateDisplay(override.rate, override.rateType, t)}
                  </ViewValue>
                </ViewSection>
              ))}
            </RatesRow>

            <TeamMembersSection
              label={t('CURRENTLY_ASSIGNED_TEAM_MEMBERS')}
              members={previousVersion?.teamMembers}
              emptyText={t('NO_TEAM_MEMBERS_ASSIGNED')}
            />

            <ViewDivider />

            <PlannedUpdateHeading level={4}>
              {t('PLANNED_RATE_UPDATE')}
            </PlannedUpdateHeading>
          </>
        )}

        <ViewSection>
          <ViewLabel level={2}>{t('EFFECTIVE_FROM')}</ViewLabel>
          <ViewValue>{effectiveFromDisplay}</ViewValue>
        </ViewSection>

        <RatesRow>
          <ViewSection>
            <ViewLabel level={2}>{t('BASE_RATE')}</ViewLabel>
            <ViewValue>
              {baseRate != null
                ? formatRateDisplay(baseRate, baseRateType, t)
                : '-'}
            </ViewValue>
          </ViewSection>

          {overrideRates.map((override: any) => (
            <ViewSection key={override.name}>
              <ViewLabel level={2}>{override.name}</ViewLabel>
              <ViewValue>
                {override.rate != null
                  ? formatRateDisplay(override.rate, override.rateType, t)
                  : '-'}
              </ViewValue>
            </ViewSection>
          ))}
        </RatesRow>

        {!previousVersion && (
          <TeamMembersSection
            label={t('CURRENTLY_ASSIGNED_TEAM_MEMBERS')}
            members={teamMembers}
            emptyText={t('NO_TEAM_MEMBERS_ASSIGNED')}
          />
        )}

        {hasHistory && (
          <>
            <ViewDivider />
            <Button
              variant="text"
              type="button"
              onClick={() => setIsHistoryOpen(true)}
            >
              <DeleteText>
                {t('VIEW_HISTORY')} <ClockCircleOutlined />
              </DeleteText>
            </Button>

            <PayRateHistoryDrawer
              cardId={cardId}
              cardName={cardTitle}
              open={isHistoryOpen}
              onClose={() => setIsHistoryOpen(false)}
            />
          </>
        )}
      </ViewFormCard>
    )
  }

  const previousVersion: PaymentCard | undefined = form.getFieldValue([
    'payRates',
    fieldName,
    'previousVersion'
  ])

  const prevBaseRate = previousVersion?.rates.find(
    (r) => r.level === OverrideLevel.BASE
  )
  const prevOverrides =
    previousVersion?.rates.filter((r) => r.level !== OverrideLevel.BASE) || []
  const prevEffectiveFromDisplay = previousVersion?.effectiveFrom
    ? `${dayjs(previousVersion.effectiveFrom).format(DATE_FORMAT)}  ${dayjs(previousVersion.effectiveFrom).format(TIME_FORMAT)}`
    : '-'

  return (
    <ViewFormCard
      level={2}
      title={previousVersion ? previousVersion.name : cardTitle}
      rightChildren={
        previousVersion ? (
          <EditActions>
            {cardId && originalCard && (
              <Button
                variant="text"
                type="button"
                icon={
                  <CloseOutlined
                    style={{ color: theme.color.neutral.color4 }}
                  />
                }
                onClick={handleCancel}
              >
                <DeleteText>{t('CANCEL_UPDATES')}</DeleteText>
              </Button>
            )}
            <Button
              variant="text"
              type="button"
              icon={<Trash color={theme.color.neutral.color4} />}
              onClick={handleDelete}
            >
              <DeleteText>{t('DELETE')}</DeleteText>
            </Button>
          </EditActions>
        ) : (
          <EditActions>
            {cardId && originalCard && (
              <Button variant="text" type="button" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            )}
            <Button
              variant="text"
              type="button"
              icon={<Trash color={theme.color.neutral.color4} />}
              onClick={handleDelete}
            >
              <DeleteText>{t('DELETE')}</DeleteText>
            </Button>
          </EditActions>
        )
      }
    >
      {previousVersion && (
        <>
          <ViewSection>
            <ViewLabel level={2}>{t('EFFECTIVE_FROM')}</ViewLabel>
            <ViewValue>{prevEffectiveFromDisplay}</ViewValue>
          </ViewSection>

          <RatesRow>
            <ViewSection>
              <ViewLabel level={2}>{t('BASE_RATE')}</ViewLabel>
              <ViewValue>
                {prevBaseRate
                  ? formatRateDisplay(
                      prevBaseRate.rate,
                      prevBaseRate.rateType,
                      t
                    )
                  : '-'}
              </ViewValue>
            </ViewSection>

            {prevOverrides.map((override) => (
              <ViewSection key={override.eventTypeId}>
                <ViewLabel level={2}>
                  {eventTypeNameMap[override.eventTypeId!] ||
                    override.eventTypeId}
                </ViewLabel>
                <ViewValue>
                  {formatRateDisplay(override.rate, override.rateType, t)}
                </ViewValue>
              </ViewSection>
            ))}
          </RatesRow>

          <TeamMembersSection
            label={t('CURRENTLY_ASSIGNED_TEAM_MEMBERS')}
            members={previousVersion?.teamMembers}
            emptyText={t('NO_TEAM_MEMBERS_ASSIGNED')}
          />

          <ViewDivider />

          <PlannedUpdateHeading level={4}>
            {t('PLANNED_RATE_UPDATE')}
          </PlannedUpdateHeading>
        </>
      )}

      <AntdForm.Item name={[fieldName, '_id']} noStyle>
        <input type="hidden" />
      </AntdForm.Item>

      <FormItem
        name={[fieldName, 'name']}
        label={<Text>{t('PAY_RATE_NAME')}</Text>}
        rules={[getRequiredRule(t('PAY_RATE_NAME')), getIsNameDifferentRule()]}
      >
        <Input maxLength={80} />
      </FormItem>

      <FormItem
        name={[fieldName, 'effectiveDate']}
        label={<Text>{t('EFFECTIVE_FROM')}</Text>}
        rules={[getRequiredRule(t('EFFECTIVE_FROM'))]}
      >
        <DatePicker format={DATE_FORMAT} />
      </FormItem>

      <FormItem
        name={[fieldName, 'effectiveTime']}
        label={<Text>{t('EFFECTIVE_TIME')}</Text>}
        rules={[getRequiredRule(t('EFFECTIVE_TIME'))]}
      >
        <TimePicker format={TIME_FORMAT} needConfirm={false} />
      </FormItem>

      <BaseRateRow>
        <FormItem
          name={[fieldName, 'rates', 0, 'rate']}
          label={<Text>{t('BASE_RATE')}</Text>}
          rules={[getRequiredRule(t('BASE_RATE'))]}
        >
          <InputNumber
            min={0}
            max={99.99}
            precision={2}
            addonAfter={
              <FormItem name={[fieldName, 'rates', 0, 'rateType']} noStyle>
                <RateTypeSelect options={rateTypeOptions} />
              </FormItem>
            }
            style={{ width: 260 }}
          />
        </FormItem>
        <AntdForm.List name={[fieldName, 'rates']}>
          {(fields, { add: addRate }) => {
            const overrideFields = fields
              .filter((field) => field.name !== 0)
              .filter((field) => filterDeletedRates(field.name))

            return (
              <>
                <Dropdown
                  menu={{
                    items: availableOptions.map((option) => ({
                      key: option.value,
                      label: option.label
                    })),
                    onClick: ({ key }) => {
                      onDirty()
                      addRate({
                        level: shiftTypeIds.has(key)
                          ? OverrideLevel.SHIFT_TYPE
                          : OverrideLevel.VISIT_TYPE,
                        eventTypeId: key,
                        rate: undefined,
                        rateType: RateType.HOURLY
                      })
                    }
                  }}
                  disabled={availableOptions.length === 0}
                >
                  <Button type="button" variant="text" icon={<Add />}>
                    {t('ADD_OVERRIDE')}
                  </Button>
                </Dropdown>

                {overrideFields.length > 0 && (
                  <OverridesSection>
                    <OverridesDivider />
                    <OverridesHeading>{t('OVERRIDES')}</OverridesHeading>
                    {overrideFields.map((overrideField) => {
                      const eventTypeId = form.getFieldValue([
                        'payRates',
                        fieldName,
                        'rates',
                        overrideField.name,
                        'eventTypeId'
                      ])
                      const eventTypeName =
                        eventTypeNameMap[eventTypeId] || eventTypeId

                      return (
                        <OverrideRow key={overrideField.key}>
                          <FormItem
                            name={[overrideField.name, 'rate']}
                            label={<Text>{eventTypeName}</Text>}
                            rules={[getRequiredRule(eventTypeName)]}
                          >
                            <InputNumber
                              min={0}
                              max={999.99}
                              precision={2}
                              addonAfter={
                                <FormItem
                                  name={[overrideField.name, 'rateType']}
                                  noStyle
                                >
                                  <RateTypeSelect options={rateTypeOptions} />
                                </FormItem>
                              }
                              style={{ width: 260 }}
                            />
                          </FormItem>
                          <Button
                            variant="text"
                            type="button"
                            icon={<Trash color={theme.color.neutral.color4} />}
                            onClick={() => handleDeleteRate(overrideField.name)}
                          >
                            <DeleteText>{t('REMOVE')}</DeleteText>
                          </Button>
                        </OverrideRow>
                      )
                    })}
                  </OverridesSection>
                )}
              </>
            )
          }}
        </AntdForm.List>
      </BaseRateRow>
    </ViewFormCard>
  )
}

const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const RatesRow = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
`

const ViewFormCard = styled(FormCard)`
  > div:last-child {
    gap: 16px;
  }
`

const ViewDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const ViewSection = styled.div``

const PlannedUpdateHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ViewLabel = styled(Text)`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const ViewValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DeleteText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const BaseRateRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  > *:first-child {
    flex: none;
  }
`

const OverridesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const OverridesDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const OverridesHeading = styled(Text)`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const OverrideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-form-item {
    flex: none;
  }
`

const RateTypeSelect = styled(AntdSelect)`
  &.ant-select {
    width: 120px;
  }
`

export default PayRateCard
